import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Info, Link2, Edit2 } from 'lucide-react';

const CustomFunctionModal = ({ isOpen, onClose, onSave, customFunction = null, nodeData = null, transitions = [], nodes = [], currentNodeId = null }) => {
    const [formData, setFormData] = useState({
        name: nodeData?.name || customFunction?.name || '',
        prompt: nodeData?.prompt || '',
        speakDuringExecution: nodeData?.speakDuringExecution || false,
        description: customFunction?.description || '',
        api_endpoint: {
            method: customFunction?.api_endpoint?.method || 'POST',
            url: customFunction?.api_endpoint?.url || ''
        },
        timeout: customFunction?.timeout || 120000,
        headers: customFunction?.headers || [],
        query_parameters: customFunction?.query_parameters || [],
        parameters: {
            type: customFunction?.parameters?.type || 'json',
            schema: customFunction?.parameters?.schema || '',
            payload_args_only: customFunction?.parameters?.payload_args_only || false
        },
        response_variables: customFunction?.response_variables || []
    });
    const [transitionsList, setTransitionsList] = useState(transitions || []);
    const [editingTransition, setEditingTransition] = useState(null);

    // Update form data when props change
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: nodeData?.name || customFunction?.name || '',
                prompt: nodeData?.prompt || '',
                speakDuringExecution: nodeData?.speakDuringExecution || false,
                description: customFunction?.description || '',
                api_endpoint: {
                    method: customFunction?.api_endpoint?.method || 'POST',
                    url: customFunction?.api_endpoint?.url || ''
                },
                timeout: customFunction?.timeout || 120000,
                headers: customFunction?.headers || [],
                query_parameters: customFunction?.query_parameters || [],
                parameters: {
                    type: customFunction?.parameters?.type || 'json',
                    schema: customFunction?.parameters?.schema || '',
                    payload_args_only: customFunction?.parameters?.payload_args_only || false
                },
                response_variables: customFunction?.response_variables || []
            });
            setTransitionsList(transitions || []);
        }
    }, [isOpen, customFunction, nodeData, transitions]);

    const [activeTab, setActiveTab] = useState('json');
    const [jsonExamples] = useState([
        {
            name: 'example 1',
            schema: JSON.stringify({
                type: 'object',
                properties: {
                    name: { type: 'string' },
                    age: { type: 'number' }
                },
                required: ['name']
            }, null, 2)
        },
        {
            name: 'example 2',
            schema: JSON.stringify({
                type: 'object',
                properties: {
                    email: { type: 'string', format: 'email' },
                    phone: { type: 'string' }
                },
                required: ['email']
            }, null, 2)
        },
        {
            name: 'example 3',
            schema: JSON.stringify({
                type: 'object',
                properties: {
                    query: { type: 'string' },
                    limit: { type: 'number', default: 10 }
                }
            }, null, 2)
        }
    ]);

    const addTransition = () => {
        const newTransition = {
            id: `trans-${Date.now()}`,
            condition: '',
            type: 'status_code',
            target_node_id: ''
        };
        setTransitionsList([...transitionsList, newTransition]);
        setEditingTransition(newTransition.id);
    };

    const updateTransition = (transitionId, updates) => {
        setTransitionsList(transitionsList.map((t) =>
            t.id === transitionId ? { ...t, ...updates } : t
        ));
    };

    const deleteTransition = (transitionId) => {
        setTransitionsList(transitionsList.filter((t) => t.id !== transitionId));
    };

    const handleSave = () => {
        // Validate required fields
        if (!formData.name.trim()) {
            alert('Name is required');
            return;
        }
        if (!formData.api_endpoint.url.trim()) {
            alert('API Endpoint URL is required');
            return;
        }

        // Separate custom function config from node data
        const customFunctionConfig = {
            name: formData.name,
            description: formData.description,
            api_endpoint: formData.api_endpoint,
            timeout: formData.timeout,
            headers: formData.headers,
            query_parameters: formData.query_parameters,
            parameters: formData.parameters,
            response_variables: formData.response_variables
        };

        // Return both node data and custom function config
        onSave({
            nodeData: {
                name: formData.name,
                prompt: formData.prompt,
                speakDuringExecution: formData.speakDuringExecution
            },
            customFunction: customFunctionConfig,
            transitions: transitionsList
        });
        onClose();
    };

    const addKeyValuePair = (type) => {
        const newPair = { key: '', value: '' };
        setFormData({
            ...formData,
            [type]: [...formData[type], newPair]
        });
    };

    const updateKeyValuePair = (type, index, field, value) => {
        const updated = [...formData[type]];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, [type]: updated });
    };

    const removeKeyValuePair = (type, index) => {
        setFormData({
            ...formData,
            [type]: formData[type].filter((_, i) => i !== index)
        });
    };

    const formatJSON = () => {
        try {
            const parsed = JSON.parse(formData.parameters.schema);
            setFormData({
                ...formData,
                parameters: {
                    ...formData.parameters,
                    schema: JSON.stringify(parsed, null, 2)
                }
            });
        } catch (e) {
            alert('Invalid JSON: ' + e.message);
        }
    };

    const loadExample = (example) => {
        setFormData({
            ...formData,
            parameters: {
                ...formData.parameters,
                schema: example.schema
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg shadow-primary/20 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <Link2 size={20} className="text-primary" />
                        <h2 className="text-xl font-semibold text-foreground">Custom Function</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter the name of the custom function"
                            className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* Prompt */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Message (Optional)
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            Message to speak during function execution (if enabled below)
                        </p>
                        <textarea
                            value={formData.prompt}
                            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                            placeholder="Enter message to speak during execution"
                            rows={2}
                            className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* Speak During Execution */}
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.speakDuringExecution}
                                onChange={(e) => setFormData({ ...formData, speakDuringExecution: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                        <span className="text-sm text-foreground">Speak message during execution</span>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Enter the description of the custom function"
                            rows={3}
                            className="w-full bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                    </div>

                    {/* API Endpoint */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            API Endpoint
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            The API Endpoint is the address of the service you are connecting to
                        </p>
                        <div className="flex gap-2">
                            <select
                                value={formData.api_endpoint.method}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    api_endpoint: { ...formData.api_endpoint, method: e.target.value }
                                })}
                                className="bg-background border border-border rounded px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="PATCH">PATCH</option>
                            </select>
                            <input
                                type="text"
                                value={formData.api_endpoint.url}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    api_endpoint: { ...formData.api_endpoint, url: e.target.value }
                                })}
                                placeholder="Enter the URL of the custom function"
                                className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Timeout */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Timeout (ms)
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                value={formData.timeout}
                                onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) || 120000 })}
                                className="w-32 bg-background border border-border rounded px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                            <span className="text-sm text-muted-foreground">milliseconds</span>
                        </div>
                    </div>

                    {/* Headers */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Headers
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            Specify the HTTP headers required for your API request.
                        </p>
                        <div className="space-y-2">
                            {formData.headers.map((header, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={header.key}
                                        onChange={(e) => updateKeyValuePair('headers', index, 'key', e.target.value)}
                                        placeholder="Key"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <input
                                        type="text"
                                        value={header.value}
                                        onChange={(e) => updateKeyValuePair('headers', index, 'value', e.target.value)}
                                        placeholder="Value"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeKeyValuePair('headers', index)}
                                        className="text-destructive hover:text-destructive/80 transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addKeyValuePair('headers')}
                                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                <Plus size={16} />
                                New key value pair
                            </button>
                        </div>
                    </div>

                    {/* Query Parameters */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Query Parameters
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            Query string parameters to append to the URL.
                        </p>
                        <div className="space-y-2">
                            {formData.query_parameters.map((param, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={param.key}
                                        onChange={(e) => updateKeyValuePair('query_parameters', index, 'key', e.target.value)}
                                        placeholder="Key"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <input
                                        type="text"
                                        value={param.value}
                                        onChange={(e) => updateKeyValuePair('query_parameters', index, 'value', e.target.value)}
                                        placeholder="Value"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeKeyValuePair('query_parameters', index)}
                                        className="text-destructive hover:text-destructive/80 transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addKeyValuePair('query_parameters')}
                                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                <Plus size={16} />
                                New key value pair
                            </button>
                        </div>
                    </div>

                    {/* Parameters (Optional) */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Parameters (Optional)
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            JSON schema that defines the format in which the LLM will return. Please refer to the{' '}
                            <a href="#" className="text-primary hover:underline">docs</a>.
                        </p>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex gap-1 border border-border rounded overflow-hidden">
                                <button
                                    onClick={() => setActiveTab('json')}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                                        activeTab === 'json'
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-background text-foreground hover:bg-muted'
                                    }`}
                                >
                                    JSON
                                </button>
                                <button
                                    onClick={() => setActiveTab('form')}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                                        activeTab === 'form'
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-background text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Form
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-foreground">Payload:</span>
                                <div className="flex items-center gap-1">
                                    <span className="text-sm text-foreground">args only</span>
                                    <Info size={14} className="text-muted-foreground" />
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.parameters.payload_args_only}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            parameters: {
                                                ...formData.parameters,
                                                payload_args_only: e.target.checked
                                            }
                                        })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                                </label>
                            </div>
                        </div>
                        {activeTab === 'json' && (
                            <div>
                                <textarea
                                    value={formData.parameters.schema}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        parameters: {
                                            ...formData.parameters,
                                            schema: e.target.value
                                        }
                                    })}
                                    placeholder="Enter JSON Schema here..."
                                    rows={8}
                                    className="w-full bg-muted/50 border border-border rounded px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <div className="flex items-center gap-2 mt-2">
                                    {jsonExamples.map((example, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => loadExample(example)}
                                            className="text-xs px-3 py-1 bg-muted hover:bg-muted/80 rounded text-foreground transition-colors"
                                        >
                                            {example.name}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={formatJSON}
                                        className="text-xs px-3 py-1 bg-primary/20 hover:bg-primary/30 rounded text-primary transition-colors"
                                    >
                                        Format JSON
                                    </button>
                                </div>
                            </div>
                        )}
                        {activeTab === 'form' && (
                            <div className="text-sm text-muted-foreground italic p-4 bg-muted/50 rounded border border-border">
                                Form view not implemented yet. Please use JSON tab.
                            </div>
                        )}
                    </div>

                    {/* Response Variables */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Response Variables
                        </label>
                        <p className="text-xs text-muted-foreground mb-2">
                            Extracted values from API response saved as dynamic variables.
                        </p>
                        <div className="space-y-2">
                            {formData.response_variables.map((variable, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={variable.key}
                                        onChange={(e) => updateKeyValuePair('response_variables', index, 'key', e.target.value)}
                                        placeholder="Variable name"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <input
                                        type="text"
                                        value={variable.value}
                                        onChange={(e) => updateKeyValuePair('response_variables', index, 'value', e.target.value)}
                                        placeholder="JSON path (e.g., data.name)"
                                        className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeKeyValuePair('response_variables', index)}
                                        className="text-destructive hover:text-destructive/80 transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addKeyValuePair('response_variables')}
                                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
                            >
                                <Plus size={16} />
                                New key value pair
                            </button>
                        </div>
                    </div>

                    {/* Transitions */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-foreground">Transitions</label>
                            <button
                                type="button"
                                onClick={addTransition}
                                className="text-primary hover:text-primary/80 text-sm flex items-center gap-1 transition-colors"
                            >
                                <Plus size={14} />
                                Add Transition
                            </button>
                        </div>

                        <div className="space-y-2">
                            {transitionsList.map((transition) => (
                                <div
                                    key={transition.id}
                                    className="border border-border rounded p-3 space-y-2 bg-muted/20"
                                >
                                    {editingTransition === transition.id ? (
                                        <>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-foreground">
                                                    Condition
                                                </label>
                                                <input
                                                    type="text"
                                                    value={transition.condition}
                                                    onChange={(e) =>
                                                        updateTransition(transition.id, {
                                                            condition: e.target.value
                                                        })
                                                    }
                                                    className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                    placeholder={
                                                        transition.type === 'status_code' 
                                                            ? 'status_code == 200' 
                                                            : transition.type === 'response_variable'
                                                            ? 'has_slots == true'
                                                            : 'User says yes'
                                                    }
                                                />
                                                {transition.type === 'response_variable' && formData.response_variables.length > 0 && (
                                                    <div className="mt-1 text-xs text-muted-foreground">
                                                        Available variables: {formData.response_variables.map(v => v.key).filter(Boolean).join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-foreground">
                                                    Type
                                                </label>
                                                <select
                                                    value={transition.type}
                                                    onChange={(e) =>
                                                        updateTransition(transition.id, {
                                                            type: e.target.value
                                                        })
                                                    }
                                                    className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="status_code">Status Code</option>
                                                    <option value="response_variable">Response Variable</option>
                                                    <option value="keyword">Keyword</option>
                                                    <option value="semantic">Semantic</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-foreground">
                                                    Target Node
                                                </label>
                                                <select
                                                    value={transition.target_node_id}
                                                    onChange={(e) =>
                                                        updateTransition(transition.id, {
                                                            target_node_id: e.target.value
                                                        })
                                                    }
                                                    className="w-full bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                                >
                                                    <option value="">Select node...</option>
                                                    {nodes
                                                        .filter((n) => n.id !== currentNodeId)
                                                        .map((n) => (
                                                            <option key={n.id} value={n.id}>
                                                                {n.data?.name || n.data?.label || n.id}
                                                                {n.data?.isEndNode ? ' (End Node)' : ''}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingTransition(null)}
                                                    className="text-sm bg-muted text-foreground px-2 py-1 rounded hover:bg-muted/80 transition-colors"
                                                >
                                                    Done
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteTransition(transition.id)}
                                                    className="text-sm bg-destructive/20 text-destructive px-2 py-1 rounded hover:bg-destructive/30 transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <div className="text-sm font-medium text-foreground">
                                                    {transition.condition || 'No condition'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {transition.type} →{' '}
                                                    {(() => {
                                                        const targetNode = nodes.find((n) => n.id === transition.target_node_id);
                                                        return targetNode ? (targetNode.data?.name || targetNode.data?.label || targetNode.id) : 'No target';
                                                    })()}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setEditingTransition(transition.id)}
                                                className="text-primary hover:text-primary/80 transition-colors"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-background border border-border rounded text-foreground hover:bg-muted transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomFunctionModal;
